/**
 * Configuration for Cost Estimation input fields
 *
 * Each field can be:
 * - enabled: whether to show the field
 * - defaultFrom: where to get default value from ('filters', 'search', null)
 * - required: whether field is required
 * - type: 'text' | 'number' | 'select' | 'link'
 */

export const estimationFieldsConfig = {
  // Fields from search page (enabled by default)
  buildingType: {
    enabled: true,
    defaultFrom: 'filters.buildingType',
    required: false,
    type: 'select',
    label: 'Gebäudetyp',
    options: [
      "1.1 Wohnen",
      "1.10 Sport, Freizeit und Erholung",
      "1.2 Unterricht, Bildung und Forschung",
      "1.3 Industrie, Handel und Verwaltung",
      "1.6 Fürsorge und Gesundheit",
      "1.9 Restauration und Hotellerie",
    ]
  },

  kanton: {
    enabled: true,
    defaultFrom: 'filters.kanton',
    required: false,
    type: 'select',
    label: 'Kanton',
    options: [
      "Aargau", "Appenzell Innerrhoden", "Appenzell Ausserrhoden",
      "Bern", "Basel-Landschaft", "Basel-Stadt", "Fribourg",
      "Genève", "Glarus", "Graubünden", "Jura", "Luzern",
      "Neuchâtel", "Nidwalden", "Obwalden", "St. Gallen",
      "Schaffhausen", "Schwyz", "Solothurn", "Thurgau",
      "Ticino", "Uri", "Vaud", "Valais", "Zug", "Zürich"
    ]
  },

  gf: {
    enabled: true,
    defaultFrom: 'filters.sizeRange',
    required: false,
    type: 'number',
    label: 'GF nach SIA 416',
    unit: 'm²',
    placeholder: 'Geschossfläche'
  },

  gv: {
    enabled: true,
    defaultFrom: 'filters.volRange',
    required: false,
    type: 'number',
    label: 'GV nach SIA 416',
    unit: 'm³',
    placeholder: 'Gebäudevolumen'
  },

  // Fields NOT on search page (disabled by default)
  projectName: {
    enabled: false,
    defaultFrom: null,
    required: false,
    type: 'text',
    label: 'Projektname',
    placeholder: 'Projektname eingeben'
  },

  projectLink: {
    enabled: false,
    defaultFrom: null,
    required: false,
    type: 'text',
    label: 'Projektablage',
    placeholder: 'Link zur Projektablage'
  },

  accuracy: {
    enabled: false,
    defaultFrom: null,
    required: false,
    type: 'select',
    label: 'Präzision',
    options: ['GKS', 'KS', 'KV'],
    default: 'KS'
  },

  costYear: {
    enabled: false,
    defaultFrom: null,
    required: false,
    type: 'number',
    label: 'Kostennormierung',
    placeholder: 'Jahr (z.B. 2024)',
    default: new Date().getFullYear()
  },

  location: {
    enabled: false,
    defaultFrom: 'filters.kanton',
    required: false,
    type: 'text',
    label: 'Ort',
    placeholder: 'Stadt/Gemeinde'
  },

  oberirdisch: {
    enabled: false,
    defaultFrom: 'filters.oberirdischRange',
    required: false,
    type: 'number',
    label: 'Geschossfläche oberirdisch',
    unit: 'm²',
    placeholder: 'Fläche oberirdisch'
  },

  hauptnutzflaeche: {
    enabled: false,
    defaultFrom: null,
    required: false,
    type: 'number',
    label: 'Hauptnutzfläche',
    unit: 'm²',
    placeholder: 'Hauptnutzfläche'
  }
};

/**
 * Get enabled fields only
 */
export const getEnabledFields = () => {
  return Object.entries(estimationFieldsConfig)
    .filter(([_, config]) => config.enabled)
    .reduce((acc, [key, config]) => {
      acc[key] = config;
      return acc;
    }, {});
};

/**
 * Get default values from filters
 */
export const getDefaultValues = (filters) => {
  const defaults = {};

  Object.entries(estimationFieldsConfig).forEach(([fieldKey, fieldConfig]) => {
    if (!fieldConfig.enabled) return;

    if (fieldConfig.defaultFrom) {
      const path = fieldConfig.defaultFrom.split('.');
      let value = filters;

      for (const key of path) {
        if (value && typeof value === 'object') {
          value = value[key];
        } else {
          value = undefined;
          break;
        }
      }

      // Handle array values (take first element for single-value fields)
      if (Array.isArray(value) && value.length > 0) {
        if (fieldConfig.type === 'select' && !Array.isArray(fieldConfig.options)) {
          defaults[fieldKey] = value[0];
        } else if (fieldConfig.type === 'number') {
          // For ranges, take the average
          defaults[fieldKey] = value.length === 2
            ? Math.round((value[0] + value[1]) / 2)
            : value[0];
        } else {
          defaults[fieldKey] = value[0];
        }
      } else if (value !== undefined && value !== null) {
        defaults[fieldKey] = value;
      }
    } else if (fieldConfig.default !== undefined) {
      defaults[fieldKey] = fieldConfig.default;
    }
  });

  return defaults;
};